// ═══════════════════════════════════════════════════════
// ULTIMAZE TOWER WARS — Core Interfaces
// Alle Interface-Änderungen hier zuerst — dann Implementierungen
// ═══════════════════════════════════════════════════════

export type Element =
  | 'fire' | 'water' | 'nature' | 'elektro' | 'light' | 'shadow' | 'neutral';

export type GameMode =
  | '1v1' | '2v2' | '3v3' | 'coop' | 'campaign';

export type Rarity =
  | 'common' | 'rare' | 'epic' | 'legendary';

export type TowerCategory =
  | 'attack' | 'terrain' | 'wall';

export type UpgradePath = 'A' | 'B' | null;

// ── GRID ──────────────────────────────────────────────
export type CellType = 'empty' | 'tower' | 'obstacle' | 'spawn' | 'base';

export interface Cell {
  x: number;
  y: number;
  type: CellType;
  walkable: boolean;
  towerId?: string;
}

export interface GridCoord {
  gx: number;
  gy: number;
}

// ── CARDS ─────────────────────────────────────────────
export interface TowerStats {
  dmg: number;
  spd: number;        // attacks per second
  range: number;      // in grid cells
  crit: number;       // 0.0–1.0
  splash?: number;
  slow?: number;
  chain?: number;
  income?: number;
  terrain?: boolean;
  projSpeed?: number; // projectile speed in pixels/sec; 0 = no projectile (terrain/wall)
}

export interface TowerCard {
  id: string;
  name: string;
  el: Element;
  cat: TowerCategory;
  cost: number;
  rarity: Rarity;
  stats: TowerStats;
  upgrades: TowerUpgrade[];
}

export interface TowerUpgrade {
  cost: number;
  desc: string;
  dmgBonus?: number;
  spdBonus?: number;
  incomeBonus?: number;
  paths?: UpgradePathDef[];
}

export interface UpgradePathDef {
  id: 'A' | 'B';
  name: string;
  desc: string;
  effect: string;
}

export interface PlacedTower extends TowerStats {
  id: string;         // card id
  instanceId: string; // unique per placement
  name: string;
  el: Element;
  cat: TowerCategory;
  cost: number;
  col: string;
  gx: number;
  gy: number;
  level: number;
  path: UpgradePath;
  lastShot: number;
  upgrades: TowerUpgrade[];
}

export interface UnitCard {
  id: string;
  name: string;
  el: Element;
  cost: number;
  rarity: Rarity;
  hp: number;
  armor: number;      // 0.0–1.0
  spd: number;        // cells per second
  income: number;     // gold/sec added permanently when sent
  dodge: number;      // 0.0–1.0
  reward: number;     // kill gold
  tier?: number;      // 1/2/3 — difficulty tier for matchmaking/filters
}

export interface ActiveUnit extends UnitCard {
  curHp: number;
  maxHp: number;
  pathIdx: number;
  /** Per-unit A* path from this unit's current cell to the base.
   *  Each unit recalculates its own path when the grid changes, so units that
   *  are already past a newly-placed tower reroute independently rather than
   *  being snapped to the shared global path. */
  path: GridCoord[];
  px: number;         // screen x
  py: number;         // screen y
  gx: number;         // grid x (approx)
  gy: number;         // grid y (approx)
  slowTimer: number;
  alive: boolean;
  ownerId: string;    // which player sent this
  /** True for units spawned via remote relay — they move visually but don't deal base damage.
   *  The sender's client is authoritative and relays HP_CHANGED events instead. */
  isGhost?: boolean;
}

// ── HERO ──────────────────────────────────────────────
export interface Hero {
  id: string;
  name: string;
  icon: string;
  class: 'offensive' | 'defensive' | 'economy' | 'support';
  passivBuff: StatModifier;
  passivDrawback: StatModifier;   // PFLICHTFELD — kein Hero ohne Drawback
  activeSpell: Spell;
  cooldownSec: number;
}

export interface StatModifier {
  description: string;
  type: 'dmgBonus' | 'speedBonus' | 'rangeBonus' | 'incomeBonus' | 'hpBonus' | 'costBonus';
  value: number;
  targetEl?: Element;
  targetCat?: TowerCategory;
}

export interface Spell {
  id: string;
  name: string;
  description: string;
  effect: string;
  cooldownSec: number;
}

// ── DECK ──────────────────────────────────────────────
export interface Deck {
  id: string;
  name: string;
  iconElement: Element;
  towers: string[];   // tower card ids (max 8)
  units: string[];    // unit card ids (max 4)
  heroId: string;
}

// ── PLAYER ────────────────────────────────────────────
export interface Player {
  id: string;
  name: string;
  deck: Deck;
  hero: Hero;
  gold: number;
  incomePerSec: number;
  hp: number;
  baseLvl: number;
  grid: Cell[][];
  isBot?: boolean;
}

// ── MATCH ─────────────────────────────────────────────
export interface MatchConfig {
  mode: GameMode;
  players: Player[];     // NEVER hardcoded to 2
  map: MapConfig;
  transport: ITransport;
  durationSecs: number;  // 900 = 15 minutes
  localPlayerId: string;
  getTowerStats?: (id: string) => TowerStats | undefined;
  /** Override random bot personality selection (used by campaign missions). */
  forcedBotPersonality?: string;
  /** Override starting bot tier (1–3). Tier 2+ starts with higher HP/speed. */
  forcedBotTier?: number;
}

export interface MapConfig {
  id: string;
  name: string;
  cols: number;
  rows: number;
  spawnX: number;
  spawnY: number;
  baseX: number;
  baseY: number;
  obstacles: GridCoord[];
  elementBuffs: ElementBuff[];
  theme: string;
}

export interface ElementBuff {
  el: Element;
  towerBonus?: number;   // multiplier on tower dmg
  unitBonus?: number;    // multiplier on unit hp
}

// ── GAME EVENTS ───────────────────────────────────────
export type GameEventType =
  | 'TOWER_PLACED' | 'TOWER_UPGRADED' | 'TOWER_DEMOLISHED'
  | 'UNIT_SENT' | 'UNIT_KILLED' | 'UNIT_REACHED_BASE'
  | 'GOLD_CHANGED' | 'HP_CHANGED' | 'BASE_UPGRADED'
  | 'SPELL_CAST' | 'MATCH_END' | 'WAVE_START';

/**
 * Payload contracts for key GameEvent types:
 *
 * GOLD_CHANGED: fired each economy tick when a player's gold changes.
 *   payload.goldAdded   = amount of gold added this tick (from income)
 *   payload.newTotal    = player's new gold total after the change
 *   payload.newIncome   = player's current income per second
 *   Note: Subscribe per-player via playerId to update the correct HUD display.
 *
 * HP_CHANGED: fired when a player loses HP (unit reaches their base).
 *   payload.playerId    = the DEFENDER (grid owner whose HP decreased)
 *   payload.newHp       = new HP value of the defender
 *   payload.delta       = HP change (negative when losing HP)
 *   payload.causedByUnit = instanceId of the unit that reached the base
 *   payload.ownerId     = playerId of the attacker who sent the unit
 *   Note: In 2v2+, filter by playerId to update the correct player's HP display.
 *
 * MATCH_END: fired once when the match concludes (defeat or timeout).
 *   payload.outcome     = 'win' | 'lose' | 'draw' (from localPlayerId's perspective)
 *   payload.reason      = 'defeat' | 'timeout' | 'quit'
 *   payload.winner      = playerId of the winner, or null if draw
 *   payload.stats       = MatchStats aggregate across all players
 *   Note: Always emitted with playerId = localPlayerId. Use payload.winner to
 *         determine the actual winner regardless of who triggered the event.
 */
export interface GameEvent {
  type: GameEventType;
  playerId: string;
  payload: Record<string, unknown>;
  timestamp: number;
}

// ── PROJECTILE ────────────────────────────────────────
export interface Projectile {
  id: string;           // unique: `${instanceId}-${targetUnitId}-${timestamp}`
  fromX: number;        // start pixel position (isometric screen coords)
  fromY: number;
  toX: number;          // target pixel position at spawn time
  toY: number;
  curX: number;         // current pixel position
  curY: number;
  speed: number;        // px/s from tower.projSpeed
  towerId: string;      // instanceId of the firing tower
  towerTypeId: string;  // card id of the firing tower (for shape selection)
  targetUnitId: string; // id of the target ActiveUnit
  el: string;           // element of the tower (for colour)
  hit: boolean;         // true when projectile has reached destination
  ownerId: string;      // playerId whose grid/towers fired this projectile
}

// ── TRANSPORT INTERFACE ───────────────────────────────
export interface ITransport {
  send(event: GameEvent): void;
  onReceive(cb: (event: GameEvent) => void): void;
  connect(): Promise<void>;
  disconnect(): void;
}

// ── PERSISTENCE INTERFACE ─────────────────────────────
export interface PlayerProfile {
  id: string;
  name: string;
  xp: number;
  level: number;
  gold: number;
  gems: number;
  decks: Deck[];          // max 5
  cardCollection: CollectedCard[];
  questProgress: QuestProgress;
  packMeter: number;      // 0–100
  lastDailyPack: string;  // ISO date string (UTC)
  stats: PlayerStats;
  language?: 'de' | 'en' | 'fr';
}

export interface CollectedCard {
  cardId: string;
  count: number;
  level: number;
}

export interface QuestProgress {
  daily: QuestEntry[];
  weekly: QuestEntry[];
  achievements: AchievementEntry[];
}

export interface QuestEntry {
  id: string;
  progress: number;
  completed: boolean;
  claimedReward: boolean;
}

export interface AchievementEntry {
  id: string;
  unlockedAt?: string;
}

export interface PlayerStats {
  matchesPlayed: number;
  matchesWon: number;
  totalDamage: number;
  totalGoldEarned: number;
  unitsSent: number;
}

export interface IPersistence {
  saveProfile(profile: PlayerProfile): Promise<void>;
  loadProfile(): Promise<PlayerProfile | null>;
  saveQuestProgress(progress: QuestProgress): Promise<void>;
  getPackMeter(): Promise<number>;
  setPackMeter(value: number): Promise<void>;
  saveMatchResult(result: MatchResult): Promise<void>;
  getRecentMatches(limit: number): Promise<MatchResult[]>;
}

// ── MATCH RESULT ──────────────────────────────────────
export interface MatchResult {
  id: string;
  timestamp: string;
  mode: GameMode;
  mapId: string;
  durationSecs: number;
  outcome: 'win' | 'lose' | 'draw';
  reason: 'defeat' | 'timeout' | 'quit';
  stats: MatchStats;
}

export interface MatchStats {
  totalDamage: number;
  towerDamage: Record<string, number>;  // towerId → damage
  goldEarned: number;
  killGold: number;
  unitsSent: number;
  towersBuilt: number;
  towersUpgraded: number;
  spellsCast: number;
}

// ── NOTIFICATION INTERFACE ────────────────────────────
export interface INotificationService {
  requestPermission(): Promise<boolean>;
  scheduleLocal(opts: {
    title: string;
    body: string;
    atDate: Date;
  }): Promise<void>;
  cancelAll(): Promise<void>;
}

// ── AUTH ──────────────────────────────────────────────
export interface AuthCredentials {
  email: string;
  password: string;
}

export interface AuthToken {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // Unix timestamp ms
}

export interface UserSession {
  userId: string;
  email: string | null;
  displayName: string;
  playerCode: string;
  emailVerified: boolean;
  token: AuthToken;
}

export interface LoginResult {
  success: boolean;
  session?: UserSession;
  error?: 'INVALID_CREDENTIALS' | 'USER_NOT_FOUND' | 'EMAIL_TAKEN' | 'NETWORK_ERROR';
}

export interface RegisterResult {
  success: boolean;
  session?: UserSession;
  error?: 'EMAIL_TAKEN' | 'WEAK_PASSWORD' | 'NETWORK_ERROR';
}

// ── LEADERBOARD ───────────────────────────────────────
export type LeagueType = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';

export const LEAGUE_THRESHOLDS: Record<LeagueType, number> = {
  bronze:   0,
  silver:   1000,
  gold:     2000,
  platinum: 3000,
  diamond:  4000,
};

export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  playerCode: string;
  country: string;
  leaguePoints: number;
  wins: number;
  losses: number;
  draws: number;
  league: LeagueType;
  rank?: number;
  isMe?: boolean;
}

export interface LeaderboardPage {
  entries: LeaderboardEntry[];
  totalCount: number;
  myEntry?: LeaderboardEntry & { rank: number };
  page?: number;
  limit?: number;
}

export const POINTS_WIN = 25;
export const POINTS_LOSS = -10;
export const POINTS_DRAW = 5;
export const POINTS_SURRENDER = -20;

// ── FRIENDS ───────────────────────────────────────────
export type FriendshipStatus = 'pending' | 'accepted' | 'blocked';

export interface FriendEntry {
  friendshipId: string;
  userId: string;
  displayName: string;
  playerCode: string;
  lastSeenAt: string | null;
  leaguePoints: number;
  wins: number;
  losses: number;
  league: LeagueType;
  isOnline?: boolean;
}

export interface FriendRequest {
  friendshipId: string;
  userId: string;
  displayName: string;
  playerCode: string;
  sentAt: string;
}

export interface FriendSearchResult {
  userId: string;
  displayName: string;
  playerCode: string;
  relationship: { status: FriendshipStatus; initiatedByMe: boolean } | null;
}

export interface FriendStats {
  userId: string;
  displayName: string;
  playerCode: string;
  country: string;
  lastSeenAt: string | null;
  memberSince: string;
  leaguePoints: number;
  wins: number;
  losses: number;
  draws: number;
  league: LeagueType;
  recentMatches: Array<{
    outcome: 'win' | 'loss' | 'draw';
    mode: string;
    durationSecs: number;
    stats: Record<string, unknown>;
    playedAt: string;
  }>;
}

// ── ASSET TEXTURE KEYS ────────────────────────────────
// Use these constants everywhere instead of magic strings.
//
// Per-entity tower atlas: public/sprites/towers/<id>.{png,json}
//   Atlas key: tower_<id>
//   Frame naming: <id>_l<level>[path]_<animation>_<frame>
//   Examples: archer_l1_idle_0  |  archer_l3a_attack_2  |  archer_l5b_idle_0
//
// Per-entity unit atlas: public/sprites/units/<id>.{png,json}
//   Atlas key: unit_<id>
//   Frame naming: <id>_<animation>_<dir>_<frame>
//   Examples: goblin_walk_e_0  |  goblin_death_3
//
// Tile sprites live at public/sprites/tiles/<key>.png
export const TEXTURE_KEYS = {
  /** Phaser texture-cache key for a per-entity tower atlas. */
  TOWER_ATLAS: (id: string): string => `tower_${id}`,

  /**
   * Frame key inside a tower atlas.
   * Level 1-2: no path suffix. Level 3-5: path suffix 'a' or 'b'.
   *   archer_l1_idle_0
   *   archer_l3a_attack_2
   *   archer_l5b_idle_0
   */
  TOWER_FRAME: (
    id: string,
    level: number,
    path: 'A' | 'B' | null,
    animation: string,
    frame: number,
  ): string => {
    const pathSuffix = level >= 3 && path !== null ? path.toLowerCase() : ''
    return `${id}_l${level}${pathSuffix}_${animation}_${frame}`
  },

  /** Phaser texture-cache key for a per-entity unit atlas. */
  UNIT_ATLAS: (id: string): string => `unit_${id}`,

  /**
   * Frame key inside a unit atlas.
   *   goblin_walk_e_0
   *   goblin_death_3
   */
  UNIT_FRAME: (
    id: string,
    animation: string,
    dir: 'e' | 'w' | 'n' | 's' | null,
    frame: number,
  ): string => dir !== null ? `${id}_${animation}_${dir}_${frame}` : `${id}_${animation}_${frame}`,

  TILE_GRASS:       'tile_grass'       as const,
  TILE_STONE:       'tile_stone'       as const,
  TILE_OBSTACLE:    'tile_obstacle'    as const,
  BASE_PLAYER:      'base_player'      as const,
  BASE_OPPONENT:    'base_opponent'    as const,
  SPAWN_MARKER:     'spawn_marker'     as const,
} as const;

// ── CARD PROGRESSION ─────────────────────────────────

/** Stat effects granted by a single skill tree node. All values are additive bonuses. */
export interface SkillNodeEffect {
  dmgBonus?: number       // +X% damage multiplier (0.20 = +20%)
  spdBonus?: number       // +X% attack-speed multiplier
  rangeBonus?: number     // +X% range multiplier
  critBonus?: number      // flat crit-chance addition (0.10 = +10%)
  chainBonus?: number     // integer bonus chain targets
  splashBonus?: number    // +X% splash-radius multiplier
  slowBonus?: number      // flat slow-amount addition
  incomeBonus?: number    // +X gold/s (for terrain towers)
  hpBonus?: number        // +X% hp multiplier (units)
  armorBonus?: number     // flat armor addition (units)
  dodgeBonus?: number     // flat dodge-chance addition (units)
  passivEnhance?: number  // +X% multiplier on hero's passive buff value
  cooldownReduction?: number // seconds of cooldown reduction (heroes)
}

export interface SkillNode {
  id: string
  name: string
  desc: string
  requires: string | null  // parent node ID that must be chosen first (null = always shown)
  effects: SkillNodeEffect
  auto?: boolean           // true = capstone, auto-unlocked without user choice
}

export interface SkillGate {
  level: number      // 3, 5, 7, or 10
  nodes: SkillNode[]
}

export interface CardLevelBonuses {
  dmgPerLevel?: number
  spdPerLevel?: number
  rangePerLevel?: number
  hpPerLevel?: number
  armorPerLevel?: number
  incomePerLevel?: number
  passivEnhancePerLevel?: number
  cooldownReductionPerLevel?: number
}

export interface CardSkillTree {
  id: string
  levelBonuses: CardLevelBonuses
  gates: SkillGate[]
}

/** Progression data for one owned card (tower, unit, or hero). */
export interface OwnedCardData {
  copies: number         // total copies drawn (level derived from this)
  chosenNodes: string[]  // skill node IDs the player has selected
}

/** Category of a card result from a pack opening. */
export type PackCardCategory = 'tower' | 'unit' | 'hero' | 'xp_bonus' | 'gem_bonus'

/** Extended pack card result that includes category and optional bonus amount. */
export interface PackCardResultV2 {
  cardId: string
  rarity: Rarity
  isNew: boolean
  category: PackCardCategory
  bonusAmount?: number  // XP or gem amount for bonus cards
}

/** Result of opening a pack with the V2 pack system. */
export interface PackResultV2 {
  packType: string
  cards: PackCardResultV2[]
}

// ── CAMPAIGN ──────────────────────────────────────────

export type CampaignObjectiveType =
  | 'destroy_base'   // win the match
  | 'kill_units'     // kill N enemy units
  | 'reach_income'   // accumulate N gold/sec income
  | 'survive';       // survive for N seconds without losing

export interface CampaignObjective {
  type: CampaignObjectiveType;
  target: number;
}

export interface CampaignReward {
  xp: number;
  gold: number;
  packType?: string;
}

export interface CampaignMissionDef {
  id: string;
  index: number;
  titleKey: string;
  descKey: string;
  objectiveKey: string;
  icon: string;
  mapId: string;
  objective: CampaignObjective;
  botPersonality: string;
  botTier: number;
  stars: number;
  reward: CampaignReward;
}

export interface CampaignSaveEntry {
  stars: number;   // 0 = failed, 1-3 = stars earned
  completed: boolean;
}

// ── ASSET MANIFEST TYPES ──────────────────────────────
// Mirrors public/sprites/manifest.json (validated by AssetRegistry at runtime).

export interface TowerManifestEntry {
  levels: number[];
  paths: string[];       // e.g. ["a","b"] or [] for towers without path splits
  animations: string[];
}

export interface UnitManifestEntry {
  directions: number;    // 4 = e/w/n/s
  animations: string[];
}

export interface AssetManifest {
  _comment?: string;
  _version?: string;
  towers: Record<string, TowerManifestEntry>;
  units: Record<string, UnitManifestEntry>;
}
